import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getSubjectPerformance } from '../apiClient';

interface SubjectData {
  name: string;
  icon: string;
  accuracy: number;
  attempted: number;
  total: number;
  color: string;
  chapters: ChapterData[];
}

interface ChapterData {
  name: string;
  accuracy: number;
  status: 'excellent' | 'good' | 'average' | 'needs-work';
}

const SubjectHeatmap: React.FC = () => {
  const [subjects, setSubjects] = useState<SubjectData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [expandedSubjects, setExpandedSubjects] = useState<Set<number>>(new Set());

  const toggleSubject = (index: number) => {
    setExpandedSubjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Subject icon and color mapping
  const subjectIconMap: { [key: string]: string } = {
    'Physics': '⚛️',
    'Chemistry': '🧪',
    'Mathematics': '📐',
    'Math': '📐',
    'Biology': '🧬',
    'English': '📚',
    'Computer Science': '💻',
    'History': '📜',
    'Geography': '🌍'
  };

  const subjectColorMap: { [key: string]: string } = {
    'Physics': '#667eea',
    'Chemistry': '#f39c12',
    'Mathematics': '#2d9cdb',
    'Math': '#2d9cdb',
    'Biology': '#27ae60',
    'English': '#e74c3c',
    'Computer Science': '#9b59b6',
    'History': '#34495e',
    'Geography': '#16a085'
  };

  const getSubjectIcon = (subjectName: string): string => {
    return subjectIconMap[subjectName] || '📖';
  };

  const getSubjectColor = (subjectName: string): string => {
    return subjectColorMap[subjectName] || '#6c757d';
  };

  const getChapterStatus = (accuracy: number): 'excellent' | 'good' | 'average' | 'needs-work' => {
    if (accuracy >= 90) return 'excellent';
    if (accuracy >= 75) return 'good';
    if (accuracy >= 60) return 'average';
    return 'needs-work';
  };

  useEffect(() => {
    const fetchSubjectPerformance = async () => {
      setIsLoading(true);
      try {
        const response = await getSubjectPerformance();
        if (response && response.result && Array.isArray(response.result)) {
          const subjectsData: SubjectData[] = response.result.map((subject: any) => ({
            name: subject.subject_name || subject.subject,
            icon: getSubjectIcon(subject.subject_name || subject.subject),
            accuracy: parseFloat(subject.accuracy) || 0,
            attempted: parseInt(subject.questions_attempted) || 0,
            total: parseInt(subject.questions_attempted) || 0,
            color: getSubjectColor(subject.subject_name || subject.subject),
            chapters: Array.isArray(subject.chapters) ? subject.chapters.map((chapter: any) => ({
              name: chapter.chapter_name,
              accuracy: parseFloat(chapter.accuracy) || 0,
              status: getChapterStatus(parseFloat(chapter.accuracy) || 0)
            })) : []
          }));
          setSubjects(subjectsData);
        } else {
          setSubjects([]);
        }
      } catch (error) {
        console.error('Failed to fetch subject performance:', error);
        setSubjects([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubjectPerformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return '#27ae60';
      case 'good': return '#2d9cdb';
      case 'average': return '#f39c12';
      case 'needs-work': return '#e74c3c';
      default: return '#6c757d';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'excellent': return 'Excellent';
      case 'good': return 'Good';
      case 'average': return 'Average';
      case 'needs-work': return 'Needs Work';
      default: return 'Not Attempted';
    }
  };

  return (
    <div className="subject-heatmap-section mb-4">
      <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
        <div className="card-header bg-transparent border-0 p-4">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h4 className="fw-bold mb-1">
                <i className="fas fa-fire me-2 text-primary"></i>
                Subject Mastery Map
              </h4>
              <small className="text-muted">Track your progress across all subjects</small>
            </div>
            <Link to="/performance" className="btn btn-sm btn-outline-primary rounded-pill px-3">
              Detailed Analysis
            </Link>
          </div>
        </div>

        <div className="card-body p-4">
          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading subject performance...</p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && subjects.length === 0 && (
            <div className="text-center py-5">
              <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">No Performance Data Yet</h5>
              <p className="text-muted">Complete some quizzes to see your subject-wise performance</p>
              <Link to="/quiz" className="btn btn-primary rounded-pill px-4">
                Start a Quiz
              </Link>
            </div>
          )}

          {/* Subjects Grid */}
          {!isLoading && subjects.length > 0 && (
            <>
              <div className="row g-4">
                {subjects.map((subject, index) => (
                <div key={index} className="col-lg-12">
                <div className="subject-card p-4 rounded-4 h-100"
                  style={{
                    background: subject.color + '08',
                    border: `2px solid ${subject.color}20`,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleSubject(index)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = `0 10px 30px ${subject.color}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                  
                  {/* Header */}
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center">
                      <span style={{ fontSize: '32px', marginRight: '12px' }}>{subject.icon}</span>
                      <div>
                        <h5 className="fw-bold mb-0">{subject.name}</h5>
                        <small className="text-muted">{subject.attempted}/{subject.total} questions</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <div className="text-end">
                        <div className="h4 fw-bold mb-0" style={{ color: subject.color }}>
                          {subject.accuracy}%
                        </div>
                        <small className="text-muted">Accuracy</small>
                      </div>
                      <div 
                        className="d-flex align-items-center justify-content-center rounded-circle ms-2"
                        style={{
                          width: '32px',
                          height: '32px',
                          background: subject.color + '20',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <i 
                          className={`fas fa-chevron-${expandedSubjects.has(index) ? 'up' : 'down'}`}
                          style={{ 
                            color: subject.color,
                            fontSize: '14px'
                          }}
                        ></i>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="progress" style={{ height: '12px', background: '#e9ecef' }}>
                      <div 
                        className="progress-bar" 
                        style={{ 
                          width: `${subject.accuracy}%`,
                          background: subject.color,
                          transition: 'width 1s ease'
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Expandable Chapters Section */}
                  {expandedSubjects.has(index) && (
                    <div className="animate__animated animate__fadeIn">
                      {/* Chapters Heatmap */}
                      <div>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <small className="fw-semibold text-muted">Chapter-wise Performance</small>
                          <small className="text-muted">{subject.chapters.length} chapters</small>
                        </div>
                        <div className="row g-2">
                          {subject.chapters.map((chapter, chapterIndex) => (
                            <div key={chapterIndex} className="col-12">
                              <div className="d-flex align-items-center justify-content-between p-2 rounded"
                                style={{
                                  background: 'white',
                                  border: `1px solid ${getStatusColor(chapter.status)}30`
                                }}>
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle me-2"
                                    style={{
                                      width: '8px',
                                      height: '8px',
                                      background: getStatusColor(chapter.status)
                                    }}></div>
                                  <small className="fw-semibold">{chapter.name}</small>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="badge rounded-pill px-2 py-1"
                                    style={{
                                      background: getStatusColor(chapter.status) + '15',
                                      color: getStatusColor(chapter.status),
                                      fontSize: '10px',
                                      fontWeight: 600
                                    }}>
                                    {chapter.accuracy}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="mt-3">
                        <Link to="/custom-quiz-history" 
                          className="btn btn-sm w-100 rounded-pill"
                          style={{
                            background: subject.color,
                            color: 'white',
                            border: 'none'
                          }}
                          onClick={(e) => e.stopPropagation()}>
                          <i className="fas fa-play me-2"></i>
                          Practice {subject.name}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
                ))}
              </div>

              {/* Legend */}
              <div className="mt-4 p-3 rounded-3" style={{ background: '#f8f9fa' }}>
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <small className="fw-semibold text-muted me-2">Performance Levels:</small>
                  {['excellent', 'good', 'average', 'needs-work'].map((status) => (
                    <div key={status} className="d-flex align-items-center">
                      <div className="rounded-circle me-2"
                        style={{
                          width: '10px',
                          height: '10px',
                          background: getStatusColor(status)
                        }}></div>
                      <small className="text-muted">{getStatusLabel(status)}</small>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubjectHeatmap;
